/**
 * PlacePhotosView — presentational photo-gallery branch of PlaceDetailModule
 * (currentView === 'photos'). State/refs/handlers stay owned by the parent and
 * are threaded down as props: the thumbnail strip/refs are created in the parent
 * so its auto-scroll and keyboard-nav effects keep mutating the same DOM nodes.
 */
import React from "react";

interface GalleryPhoto {
  fullsizeUrl: string;
  thumbnailUrl?: string;
  caption?: string;
  isMap?: boolean;
}

interface PlacePhotosViewProps {
  galleryPhotos: GalleryPhoto[];
  photoModalIndex: number;
  setPhotoModalIndex: (index: number) => void;
  onBack: () => void;
  onClose?: () => void;
  copiedLink: boolean;
  setCopiedLink: (copied: boolean) => void;
  thumbnailStripRef: React.RefObject<HTMLDivElement | null>;
  thumbnailRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>;
  isMobile: boolean;
}

export function PlacePhotosView({
  galleryPhotos,
  photoModalIndex,
  setPhotoModalIndex,
  onBack,
  copiedLink,
  setCopiedLink,
  thumbnailStripRef,
  thumbnailRefs,
  isMobile,
}: PlacePhotosViewProps) {
  const currentPhoto = galleryPhotos[photoModalIndex];

  return (
    <div
      style={{
        height: isMobile ? "100dvh" : "75vh", // Full height on mobile, 75vh on desktop
        width: isMobile ? "100vw" : "100%", // Full width on mobile
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: isMobile ? "fixed" : "relative",
        top: isMobile ? 0 : "auto",
        left: isMobile ? 0 : "auto",
        right: isMobile ? 0 : "auto",
        bottom: isMobile ? 0 : "auto",
        borderRadius: isMobile ? "0" : "12px",
        zIndex: isMobile ? 10000 : "auto",
      }}
    >
      {/* Header with back button */}
      <div
        style={{
          position: "absolute",
          top: "16px",
          left: "16px",
          right: "16px",
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "var(--color-surface)",
            border: "none",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
          title="Back"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
        </button>
      </div>

      {/* Copy link button for current photo — positioned below back button on left */}
      <button
        onClick={async () => {
          const url =
            currentPhoto.fullsizeUrl || currentPhoto.thumbnailUrl || "";
          if (!url) return;
          try {
            await navigator.clipboard.writeText(url);
            setCopiedLink(true);
            setTimeout(() => setCopiedLink(false), 2000);
          } catch {
            /* clipboard unavailable */
          }
        }}
        style={{
          position: "absolute",
          top: "56px",
          left: "16px",
          zIndex: 10,
          background: copiedLink ? "var(--color-primary)" : "white",
          color: copiedLink ? "white" : "#333",
          border: "none",
          borderRadius: "8px",
          padding: "8px 14px",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          cursor: "pointer",
          boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
          transition: "all 0.2s",
          fontSize: "13px",
          fontWeight: 500,
        }}
        title={copiedLink ? "Copied!" : "Copy image link"}
      >
        {copiedLink ? (
          <>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            Copied!
          </>
        ) : (
          <>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            Copy Link
          </>
        )}
      </button>

      {/* Main photo - constrained to avoid vertical scrolling */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isMobile ? "60px 16px 0px" : "60px 80px 0px",
          background: "var(--color-surface-hover)",
          overflow: "hidden",
          minHeight: 0, // Critical for flexbox to respect constraints
          borderRadius: isMobile ? "0" : "12px 12px 0 0", // Rounded top corners on desktop only
        }}
      >
        <img
          src={currentPhoto.fullsizeUrl}
          alt={currentPhoto.caption || `Photo ${photoModalIndex + 1}`}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            width: "auto",
            height: "auto",
            objectFit: "contain",
            borderRadius: "8px",
          }}
        />
      </div>

      {/* Navigation arrows */}
      {photoModalIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setPhotoModalIndex(photoModalIndex - 1);
          }}
          style={{
            position: "absolute",
            left: "16px",
            top: "50%",
            transform: "translateY(-50%)",
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: "50%",
            width: "48px",
            height: "48px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = "var(--color-surface-muted)")
          }
          onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
      )}
      {photoModalIndex < galleryPhotos.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setPhotoModalIndex(photoModalIndex + 1);
          }}
          style={{
            position: "absolute",
            right: "16px",
            top: "50%",
            transform: "translateY(-50%)",
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: "50%",
            width: "48px",
            height: "48px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = "var(--color-surface-muted)")
          }
          onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      )}

      {/* Thumbnail strip - fixed at bottom, with auto-scroll */}
      <div
        ref={thumbnailStripRef}
        style={{
          height: "120px",
          flexShrink: 0, // Don't let this shrink
          background: "var(--color-surface)",
          borderTop: "1px solid var(--color-border)",
          overflowX: "auto",
          overflowY: "hidden",
          padding: "12px",
          borderRadius: isMobile ? "0" : "0 0 12px 12px", // Rounded bottom corners on desktop only
        }}
      >
        <div style={{ display: "flex", gap: "8px", height: "100%" }}>
          {galleryPhotos.map((photo, idx) => (
            <button
              key={idx}
              ref={(el) => {
                thumbnailRefs.current[idx] = el;
              }}
              onClick={() => setPhotoModalIndex(idx)}
              style={{
                flex: "0 0 auto",
                height: "100%",
                aspectRatio: "1",
                border:
                  idx === photoModalIndex
                    ? "2px solid var(--color-primary)"
                    : "2px solid transparent",
                borderRadius: "8px",
                overflow: "hidden",
                cursor: "pointer",
                opacity: idx === photoModalIndex ? 1 : 0.6,
                transition: "all 0.2s",
              }}
            >
              <img
                src={photo.thumbnailUrl}
                alt={`Thumbnail ${idx + 1}`}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
